import React, { useMemo, useState } from "react";
import { Check, Copy, Loader2, Search } from "lucide-react";

import { COPY } from "./copy";
import type { Ga4AccountSummary, Ga4PropertySummary } from "./ga4Api";
import { PROPERTY_SEARCH_THRESHOLD } from "./fixtures/oauth-demo";

/** Property ID is what support asks for, so it's shown, not hidden behind a hover. */
function PropertyIdChip({ propertyId }: { propertyId: string }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigator.clipboard
                    ?.writeText(propertyId)
                    .then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1600);
                    })
                    .catch(() => {
                        /* clipboard blocked — the id is still selectable */
                    });
            }}
            title={COPY.wizard.step1.copyPropertyId}
            className="inline-flex items-center gap-1 rounded font-mono text-[11px] text-gray-600 transition-colors hover:text-geekblue-600">
            {COPY.wizard.step1.propertyLabel(propertyId)}
            {copied ? (
                <Check className="h-3 w-3 text-emerald-600" strokeWidth={2.5} />
            ) : (
                <Copy className="h-2.5 w-2.5 opacity-50" strokeWidth={2} />
            )}
        </button>
    );
}

/**
 * Live property picker, driven by the `accounts` probe.
 *
 * Single-select: choosing a property triggers the second setup-details call, and
 * every downstream probe is scoped to one property.
 */
export function Ga4LivePropertyPicker({
    accounts,
    selectedId,
    loading,
    onSelect
}: {
    accounts: Ga4AccountSummary[];
    selectedId: string | null;
    loading: boolean;
    onSelect: (property: Ga4PropertySummary) => void;
}) {
    const [query, setQuery] = useState("");

    const total = useMemo(() => accounts.reduce((n, a) => n + a.properties.length, 0), [accounts]);
    const showSearch = total > PROPERTY_SEARCH_THRESHOLD;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return accounts;
        return accounts
            .map((a) => ({
                ...a,
                properties: a.properties.filter(
                    (p) => p.displayName.toLowerCase().includes(q) || p.propertyId.includes(q)
                )
            }))
            .filter((a) => a.properties.length > 0);
    }, [accounts, query]);

    if (accounts.length === 0) return null;

    return (
        <div className="mt-5">
            <p className="text-[13px] font-medium text-gray-900">{COPY.wizard.step1.pickerHeading}</p>
            <p className="mt-0.5 text-[12.5px] text-gray-500">{COPY.wizard.step1.pickerSubheading}</p>

            {showSearch && (
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5">
                    <Search className="h-3.5 w-3.5 flex-none text-gray-400" strokeWidth={2} />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={COPY.wizard.step1.pickerSearchPlaceholder}
                        className="w-full text-[12.5px] text-gray-900 focus:outline-none"
                    />
                </div>
            )}

            <div className="mt-3 space-y-4">
                {filtered.map((group) => (
                    <div key={group.accountId}>
                        <div className="flex items-baseline justify-between gap-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {group.accountName}
                            </p>
                            <p className="font-mono text-[10.5px] text-gray-400">
                                {COPY.wizard.step1.accountLabel(group.accountId)}
                            </p>
                        </div>
                        <div className="mt-1.5 space-y-2">
                            {group.properties.map((p) => {
                                const selected = selectedId === p.propertyId;
                                return (
                                    <label
                                        key={p.propertyId}
                                        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${
                                            selected
                                                ? "border-geekblue-400 bg-geekblue-100"
                                                : "border-gray-200 bg-white hover:border-gray-300"
                                        }`}>
                                        <input
                                            type="radio"
                                            name="ga4-live-property"
                                            className="mt-1 h-3.5 w-3.5 accent-geekblue-600"
                                            checked={selected}
                                            disabled={loading}
                                            onChange={() => onSelect(p)}
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-baseline justify-between gap-2">
                                                <span className="truncate text-[13px] font-medium text-gray-900">
                                                    {p.displayName}
                                                </span>
                                                <span className="flex-none text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">
                                                    {p.propertyType.replace(/^PROPERTY_TYPE_/, "")}
                                                </span>
                                            </span>
                                            <span className="mt-0.5 block">
                                                <PropertyIdChip propertyId={p.propertyId} />
                                            </span>
                                        </span>
                                        {selected && loading && (
                                            <Loader2 className="mt-1 h-3.5 w-3.5 flex-none animate-spin text-geekblue-500" />
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default Ga4LivePropertyPicker;
